import {DataTable, fullDateTime, name} from '@management-ui/core';
import {DownloadForOffline} from '@mui/icons-material';
import {Box, Button, Typography} from '@mui/material';
import {reverse} from 'named-urls';
import React, {useCallback, useContext, useMemo, useRef} from 'react';
import {useSelector} from 'react-redux';
import {useHistory} from 'react-router-dom';
import {ServiceContext} from '../../../../components/Services';
import routes from '../../../../routes';
import NewContactForm from '../../forms/NewContactForm';

export default function Table(
  {
    title = 'Contacts',
    archive = false,
    canCreate = true,
    canExport = false,
    onSelect = null,
    options = {},
    company = null,
    endUsers = undefined,
  }
) {
  // noinspection DuplicatedCode
  const services = useContext(ServiceContext);
  const history = useHistory();
  const user = useSelector(state => state['auth'].user);

  const lastQuery = useRef(null);
  const lastFilters = useRef(null);

  const handleExport = useCallback(() => {
    services.contact.getTableContacts(lastQuery.current, lastFilters.current, 'All Contacts')
      .then(() => null)
      .catch(() => null);
  }, [services]);

  const handleSelect = useCallback((contact) => {
    if (onSelect) {
      onSelect(contact)
    } else {
      history.push(reverse(`${archive ? routes.archive.contacts.detail : routes.contacts.detail}`, {id: contact.id}));
    }
  }, [history, archive, onSelect]);

  const loadData = useCallback(query => new Promise((resolve, reject) => {
    const filters = {
      ...(archive ? {archive: 'only'} : {}),
      ...(company ? {company: company.id} : {}),
      ...(endUsers === undefined ? {} : {end_users: endUsers ? '1' : '0'}),
    };
    lastQuery.current = query;
    lastFilters.current = filters;
    services.contact.getTableContacts(query, filters)
      .then(response => {
        resolve({
          data: response.data, page: response.meta.current_page - 1, totalCount: response.meta.total
        });
      }).catch(() => {
      reject();
    });
  }), [services, archive, company, endUsers])

  const tableProps = useMemo(() => ({
      title: (
        <Box display="flex">
          <Typography marginRight={2} variant="h6">{title}</Typography>
          {canExport && user?.is_superuser ? (
            <Button
              variant="contained"
              color="info"
              startIcon={<DownloadForOffline/>}
              onClick={handleExport}
              size="small"
            >
              Export
            </Button>
          ) : null}
        </Box>
      ),
      columns:
        [
          {title: 'Name', field: 'name', render: data => name(data)},
          {title: 'Email Address', field: 'email'},
          {title: 'Telephone', field: 'phone'},
          ...(company ? [] : [{
            title: 'Companies',
            field: 'associations',
            render: data => data.associations ? data.associations.join(', ') : ''
          }]),
          {title: 'Date Added', field: 'created_at', render: data => fullDateTime(data.created_at)},
          ...(archive ? [{title: 'Archived', field: 'deleted_at', render: data => fullDateTime(data.deleted_at)}] : [])
        ],
      loadData
    }),
    [title, canExport, archive, company, user, handleExport, loadData]
  );

  return (
    <DataTable
      options={{
        ...options,
        headerStyle: {background: 'white', position: 'sticky', top: 0},
        maxBodyHeight: 'calc(100vh - 280px)'
      }}
      {...tableProps}
      onRowClick={handleSelect}
      newForm={canCreate && archive ? null : {
        title: 'Add New Contact',
        render: (props) => <NewContactForm {...props}/>,
        onSaved: handleSelect
      }}
    />
  );
}
